import { useMemo } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import {
  Building2, CircleDollarSign, LayoutDashboard, Megaphone, Users, Network,
  Wallet, FileText, Settings, CalendarClock, HandCoins, ShieldAlert, Scale,
} from 'lucide-react'
import type { ContextualNav, NavItem } from '@/components/shell/MainNav'

interface UseContextualNavOptions {
  /** Name of the currently selected association (from loaded data) */
  associationName?: string | null
  /** Name of the currently selected circle (from loaded data) */
  circleName?: string | null
}

// `href` overrides the default `/associations?view=…` link (used for Events,
// which lives on the global Events page rather than an association sub-view).
const ASSOCIATION_SUB_VIEWS: { label: string; view: string; icon: typeof LayoutDashboard; href?: string }[] = [
  { label: 'Dashboard', view: 'dashboard', icon: LayoutDashboard },
  { label: 'Announcements', view: 'announcements', icon: Megaphone },
  { label: 'Members', view: 'members', icon: Users },
  { label: 'Finance', view: 'finances', icon: Wallet },
  { label: 'Events', view: 'events', icon: CalendarClock },
  { label: 'Documents', view: 'documents', icon: FileText },
  { label: 'Circles', view: 'circles', icon: CircleDollarSign },
  { label: 'Network', view: 'network', icon: Network },
  // P-E2/P-E3 — governance was real but absent from every nav surface, and
  // Administration was reachable only through dashboard buttons.
  { label: 'Governance', view: 'governance', icon: Scale, href: '/governance' },
  { label: 'Administration', view: 'administration', icon: ShieldAlert },
  { label: 'Settings', view: 'settings', icon: Settings },
]

const CIRCLE_SUB_VIEWS: { label: string; view: string; icon: typeof LayoutDashboard }[] = [
  { label: 'Dashboard', view: 'detail', icon: LayoutDashboard },
  { label: 'Participants', view: 'manage', icon: Users },
  { label: 'Payout Schedule', view: 'payout-schedule', icon: CalendarClock },
  { label: 'Contributions', view: 'contribute', icon: HandCoins },
  { label: 'Finance', view: 'treasurer', icon: Wallet },
  { label: 'Risks', view: 'risk-scores', icon: ShieldAlert },
  { label: 'Disputes', view: 'disputes', icon: Scale },
  { label: 'Documents', view: 'documents', icon: FileText },
  { label: 'Cash Collection', view: 'cash-collection', icon: HandCoins },
  { label: 'Settings', view: 'settings', icon: Settings },
]

export function useContextualNav(options?: UseContextualNavOptions): ContextualNav | null {
  const location = useLocation()
  const [searchParams] = useSearchParams()

  const associationName = options?.associationName ?? null
  const circleName = options?.circleName ?? null

  return useMemo(() => {
    const path = location.pathname
    const entityId = searchParams.get('id')
    const currentView = searchParams.get('view')

    // Only show contextual nav when an entity is selected (has id param)
    if (!entityId) return null

    if (path === '/associations' || path.startsWith('/associations/')) {
      // Must be on a sub-view (not the list)
      if (!currentView || currentView === 'list' || currentView === 'discover' || currentView === 'create') {
        return null
      }

      const items: NavItem[] = ASSOCIATION_SUB_VIEWS.map((sv) => ({
        label: sv.label,
        href: sv.href ?? `/associations?view=${sv.view}&id=${entityId}`,
        icon: sv.icon,
        isActive: sv.href ? path.startsWith(sv.href) : currentView === sv.view,
        accessMode: 'read_write' as const,
      }))

      return {
        entityName: associationName || 'Association',
        entityIcon: Building2,
        entityId,
        entityType: 'association',
        items,
      }
    }

    if (path === '/circles' || path.startsWith('/circles/')) {
      // Must be on a sub-view (not the dashboard/list)
      if (!currentView || currentView === 'dashboard' || currentView === 'discover' || currentView === 'create' || currentView === 'invitations') {
        return null
      }

      const items: NavItem[] = CIRCLE_SUB_VIEWS.map((sv) => ({
        label: sv.label,
        href: `/circles?view=${sv.view}&id=${entityId}`,
        icon: sv.icon,
        isActive: currentView === sv.view,
        accessMode: 'read_write' as const,
      }))

      return {
        entityName: circleName || 'Circle',
        entityIcon: CircleDollarSign,
        entityId,
        entityType: 'circle',
        items,
      }
    }

    return null
  }, [location.pathname, searchParams, associationName, circleName])
}
